import type { Routes } from '@angular/router';
import {
  CABINET_SHELL_OWNS_BACK,
  CABINET_SHELL_OWNS_TITLE,
} from '../layout/cabinet-shell-navigation.token';
import { CabinetShellChromeService } from '../layout/cabinet-shell-chrome.service';
import { CABINET_CLIENT_SHELL_SHOW_WEB_HEADER } from '../layout/cabinet-shell-web-header.token';
import { provideSelfCabinetProxy } from '../proxy/cabinet-proxy.providers';
import type {
  ClientCabinetPageRoutesOptions,
  ClientCabinetRouteData,
  ClientCabinetRoutesOptions,
} from './client-cabinet-routes.types';

function withMobileHeaderData(
  data: ClientCabinetRouteData,
  enabled: boolean,
): ClientCabinetRouteData | undefined {
  return enabled ? data : undefined;
}

/** Shared client cabinet page routes — single source of truth for web and mobile. */
export function createClientCabinetPageRoutes(
  options: ClientCabinetPageRoutesOptions = {},
): Routes {
  const mobileHeaderData = options.mobileHeaderData ?? false;

  return [
    {
      path: '',
      loadComponent: () =>
        import('../pages/home/dashboard-client-home-page').then(
          (m) => m.DashboardClientHomePage,
        ),
      data: withMobileHeaderData({ mobileHeader: { kind: 'home' } }, mobileHeaderData),
    },
    {
      path: 'tasks',
      loadComponent: () =>
        import('../pages/tasks/dashboard-client-tasks-page').then(
          (m) => m.DashboardClientTasksPage,
        ),
      data: withMobileHeaderData(
        { mobileHeader: { kind: 'nested', backRoute: '' } },
        mobileHeaderData,
      ),
    },
    {
      path: 'profile',
      loadChildren: () =>
        import('../pages/profile/dashboard-client-profile.routes').then(
          (m) => m.dashboardClientProfileRoutes,
        ),
    },
    {
      path: 'my-storage',
      loadChildren: () =>
        import('../pages/my-storage/dashboard-client-my-storage.routes').then(
          (m) => m.dashboardClientMyStorageRoutes,
        ),
    },
    {
      path: 'wallet/statement',
      loadComponent: () =>
        import('../pages/wallet/dashboard-client-wallet-statement-page').then(
          (m) => m.DashboardClientWalletStatementPage,
        ),
      data: withMobileHeaderData(
        { mobileHeader: { kind: 'nested', backRoute: 'wallet' } },
        mobileHeaderData,
      ),
    },
    {
      path: 'wallet',
      loadComponent: () =>
        import('../pages/wallet/dashboard-client-wallet-page').then(
          (m) => m.DashboardClientWalletPage,
        ),
      data: withMobileHeaderData({ mobileHeader: { kind: 'tabRoot' } }, mobileHeaderData),
    },
    {
      path: 'requests',
      loadChildren: () =>
        import('../pages/requests/dashboard-client-requests.routes').then(
          (m) => m.dashboardClientRequestsRoutes,
        ),
    },
    {
      path: 'tariffs',
      loadChildren: () =>
        import('../pages/tariffs/dashboard-client-tariffs.routes').then(
          (m) => m.dashboardClientTariffsRoutes,
        ),
    },
    {
      path: 'my-orders',
      redirectTo: 'requests',
      pathMatch: 'full',
    },
    {
      path: 'my-addresses',
      redirectTo: 'profile/my-addresses',
      pathMatch: 'full',
    },
    {
      path: 'my-addresses/create',
      redirectTo: 'profile/my-addresses',
      pathMatch: 'full',
    },
    {
      path: 'my-addresses/:id/edit',
      redirectTo: 'profile/my-addresses',
    },
    {
      path: 'profile/requests',
      redirectTo: 'requests',
      pathMatch: 'full',
    },
    {
      path: 'profile/requests/:id',
      redirectTo: 'requests/:id',
    },
    {
      path: 'help',
      loadChildren: () =>
        import('../pages/help/dashboard-client-help.routes').then(
          (m) => m.dashboardClientHelpRoutes,
        ),
    },
    {
      path: 'wallet/topup/result',
      redirectTo: '',
      pathMatch: 'full',
    },
  ];
}

/** Mounts shared client cabinet routes under an app-specific shell. */
export function createClientCabinetRoutes(
  options: ClientCabinetRoutesOptions,
): Routes {
  if (!options.shellComponent && !options.shellLoadComponent) {
    throw new Error(
      'createClientCabinetRoutes requires shellComponent or shellLoadComponent',
    );
  }

  const shellRoute: Routes[number] = {
    path: '',
    providers: options.shellProviders,
    children: createClientCabinetPageRoutes({
      mobileHeaderData: options.mobileHeaderData,
    }),
  };

  if (options.shellLoadComponent) {
    shellRoute.loadComponent = options.shellLoadComponent;
  } else if (options.shellComponent) {
    shellRoute.component = options.shellComponent;
  }

  return [shellRoute];
}

export const WEB_CLIENT_CABINET_SHELL_PROVIDERS = [
  CabinetShellChromeService,
  { provide: CABINET_SHELL_OWNS_BACK, useValue: true },
  { provide: CABINET_SHELL_OWNS_TITLE, useValue: true },
  { provide: CABINET_CLIENT_SHELL_SHOW_WEB_HEADER, useValue: true },
  ...provideSelfCabinetProxy(),
] as const;

export const MOBILE_CLIENT_CABINET_SHELL_PROVIDERS = [
  CabinetShellChromeService,
  { provide: CABINET_SHELL_OWNS_BACK, useValue: true },
  { provide: CABINET_SHELL_OWNS_TITLE, useValue: true },
  ...provideSelfCabinetProxy(),
] as const;

export {
  myStorageRequestRedirectGuard,
  MyStorageRequestRedirectComponent,
} from './my-storage-request-redirect.guard';
